import { useState, useEffect, useRef } from 'react'
import { ChatMessage } from '@/types/ChatContracts'
import useCentralizedChatManager from '@/components/useCentralizedChatManager'
import useAppNotificationActions from '@/functionalities/AppNotification/useAppNotificationActions'
import { AnalyticsEvents } from '@/services/AnalyticsService'
import useAnalytics from '@/services/useAnalytics'
import useMessageBatching from './internal/useMessageBatching'
import { ChatboxTabTypes, ChatboxSnackbarInfo } from './ChatRoom'
import { logger } from '@/services/Logger'

const log = logger.createChildLogger({ name: 'useChatRoomController' })

export default function useChatRoomController(chatManager: ReturnType<typeof useCentralizedChatManager>) {
    const { isLoading, messageHistory } = chatManager

    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [currentTab, setCurrentTab] = useState<ChatboxTabTypes>('messages')
    const [unreadMessagesCt, _setUnreadMessagesCt] = useState<number>(0)
    const [currentSelectedFiles, setCurrentSelectedFiles] = useState<{ [key: string]: boolean }>({})
    const [chatboxSnackbar, setChatboxSnackbar] = useState<ChatboxSnackbarInfo | null>(null)

    const scrollContainerRef = useRef<HTMLDivElement>(null)

    const downloadLockedFilesRef = useRef<Set<string>>(new Set())
    const messageHistoryLengthRef = useRef(0)
    const doBlockDragModeRef = useRef(false)

    const { logUserActivityWithContext } = useAnalytics()
    const { showAppNotification } = useAppNotificationActions()

    // message batching
    const {
        hasMoreText,
        msgLoadedBatchState,
        onScroll: messageBatchingOnScroll,
        onScrollToBottom: messageBatchingOnScrollToBottom,
        onMessageListUpdated: messageBatchingOnMessageListUpdated,
    } = useMessageBatching()

    const setUnreadMessagesCt = (ct: number) => {
        _setUnreadMessagesCt(ct)
    }

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current

        // Register scroll handler
        if (!scrollContainer) {
            return
        }

        const scrollHandler = (e: any) => {
            messageBatchingOnScroll(e, messageHistoryLengthRef.current)
        }

        // Monitor scroll changes
        scrollContainer.addEventListener('scroll', scrollHandler, {
            capture: true, // Get everything that scrolls inside the div
        })

        return () => {
            if (scrollContainer) {
                scrollContainer.removeEventListener('scroll', scrollHandler)
            }
        }

        // isOpen: Reload when the chatbox is opened/closed
    }, [isOpen, scrollContainerRef.current])

    const chatboxNotificationRefractoryTimerRef = useRef<NodeJS.Timeout | null>(null)

    useEffect(() => {
        if (isLoading) {
            return
        }

        chatManager.setOnFileDownloadCompleteHandler((fileUuid: string, filename: string) => {
            downloadLockedFilesRef.current.delete(fileUuid)
            log.verbose(
                'setOnFileDownloadCompleteHandler',
                `clearing fileUuid="${fileUuid}" from downloadLockedFilesRef as download has completed.`
            )

            if (!chatboxNotificationRefractoryTimerRef.current) {
                showAppNotification({
                    title: 'File downloaded',
                    body: `"${filename || 'A file'}" completed downloading.`,
                })
                chatboxNotificationRefractoryTimerRef.current = setTimeout(() => {
                    chatboxNotificationRefractoryTimerRef.current = null
                }, 5000)
            }
        })
    }, [isLoading])

    useEffect(() => {
        if (isLoading) {
            return
        }

        chatManager.setOnNewMessageHandler((chatmsg: ChatMessage) => {
            if (!isOpen) {
                // Show notification
                if (!chatboxNotificationRefractoryTimerRef.current) {
                    console.log(
                        `chatbox: at chatManager.setOnNewMessageHandler callback: calling ElectronIPC.showNotification().`
                    )
                    if (chatmsg.contentType === 'text') {
                        showAppNotification({ title: 'New Message', body: chatmsg.body })
                    } else if (chatmsg.contentType === 'file') {
                        showAppNotification({
                            title: 'File Uploaded',
                            body: `"${chatmsg.body.filename}" has been uploaded.`,
                        })
                    }
                    chatboxNotificationRefractoryTimerRef.current = setTimeout(() => {
                        chatboxNotificationRefractoryTimerRef.current = null
                    }, 5000)
                }

                setUnreadMessagesCt(unreadMessagesCt + 1)
            }
        })
    }, [isOpen, unreadMessagesCt, isLoading])

    useEffect(() => {
        if (isOpen) {
            scrollToBottom()
        }
        // NOTE: scrollToBottom() is called additionally when `currentTab` changes.
    }, [isOpen, currentTab])

    const scrollToBottom = () => {
        log.verbose('scrollToBottom', 'called.')

        if (isOpen) {
            setUnreadMessagesCt(0)
        }
        scrollToBottomAction()

        messageBatchingOnScrollToBottom(messageHistoryLengthRef.current)
    }

    const onCellSelectedHandler = (id: string, is: boolean) => {
        if (is) {
            setCurrentSelectedFiles((set) => ({ ...set, [id]: true }))
        } else {
            setCurrentSelectedFiles((set) => {
                const next = { ...set }
                delete next[id]
                return next
            })
        }
    }

    const onDeleteSelectedCellsHandler = () => {
        chatManager?.deleteMessages(Object.keys(currentSelectedFiles))
        setCurrentSelectedFiles({})

        logUserActivityWithContext(AnalyticsEvents.DELETED_CHAT_MESSAGE)
    }

    const scrollToBottomAction = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight
        }
    }

    // if messageHistory + messagePendingLookup length changes, update scroll-to-bottom
    const prevListLength = useRef(0)
    useEffect(() => {
        messageHistoryLengthRef.current = messageHistory.length

        const pendingMessagesCt = chatManager.messagePendingLookup.size || 0
        const listLength = messageHistory.length + pendingMessagesCt

        // if not at bottom, don't auto scroll.
        if (hasMoreText) {
            // no-op
        } else {
            if (listLength > prevListLength.current) {
                scrollToBottom()
            }
        }

        prevListLength.current = listLength

        // update messageHistory headIdx when message history is updated.
        messageBatchingOnMessageListUpdated(messageHistory.length)
    }, [messageHistory, chatManager.messagePendingLookup, hasMoreText])

    return {
        chatManager,
        messageHistory,
        msgLoadedBatchState,
        chatboxSnackbar,
        currentSelectedFiles,
        scrollContainerRef,
        downloadLockedFilesRef,
        doBlockDragModeRef,
        isOpen,
        unreadMessagesCt,

        setChatboxSnackbar,
        setIsOpen,
        setCurrentTab,

        onCellSelectedHandler,
        onDeleteSelectedCellsHandler,
    }
}
